import { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { AuthUser } from '../types';

export class AuthService {
  private supabase: SupabaseClient;

  constructor() {
    this.supabase = supabase;
  }

  async signIn(email: string, password: string): Promise<AuthUser | null> {
    const { data, error } = await this.supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) throw error;
    if (!data.user) return null;

    // Check if user is a partner
    const isPartner = await this.isPartner(data.user.id);
    if (!isPartner) {
      await this.signOut();
      throw new Error('Access denied. Partner account required.');
    }

    return {
      id: data.user.id,
      email: data.user.email!
    };
  }

  async signOut(): Promise<void> {
    const { error } = await this.supabase.auth.signOut();
    if (error) throw error;
  }

  async getCurrentUser(): Promise<AuthUser | null> {
    const { data: { user } } = await this.supabase.auth.getUser();
    
    if (!user) return null;

    // Check if user is still a partner
    const isPartner = await this.isPartner(user.id);
    if (!isPartner) {
      await this.signOut();
      return null;
    }

    return {
      id: user.id,
      email: user.email!
    };
  }

  private async isPartner(userId: string): Promise<boolean> {
    const { data, error } = await this.supabase
      .from('partners')
      .select('id')
      .eq('id', userId)
      .eq('is_active', true)
      .maybeSingle(); // Use maybeSingle() instead of single() to handle zero results

    if (error) {
      console.error('Error checking partner status:', error);
      return false;
    }

    return !!data;
  }

  onAuthStateChange(callback: (user: AuthUser | null) => void): () => void {
    const { data: { subscription } } = this.supabase.auth.onAuthStateChange(
      async (event: any, session: any) => {
        if (event === 'SIGNED_IN' && session?.user) {
          const isPartner = await this.isPartner(session.user.id);
          if (isPartner) {
            callback({
              id: session.user.id,
              email: session.user.email!
            });
          } else {
            await this.signOut();
            callback(null);
          }
        } else if (event === 'SIGNED_OUT') {
          callback(null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }
}
